/***
 *
 *   APP NAV
 *   The app sidebar. Ported from
 *   ui-reference/src/components/layouts/{sidebar,sidebar-nav-item}.tsx.
 *
 *   Desktop only (`hidden md:flex`) — below md the mobile bottom bar
 *   (components/nav/mobile/mobileBar.js) replaces it entirely, which is what
 *   retired the old `.popup` drawer in app.scss. Sits in the layout flow as
 *   the left strip of the primary-coloured frame, not `fixed`.
 *
 *   Collapsed / expanded lives in context/SidebarContext.js, shared with the
 *   collapse toggle in the top bar and persisted to localStorage.
 *
 *   PROPS
 *   items: array of { key, label, icon, link, action, external }
 *
 **********/

import { NavLink, useLocation } from 'react-router-dom';
import { Icon, Tooltip, cn } from 'components/lib';
import { useTranslate } from 'app/translations';
import { useSidebarContext } from 'context/SidebarContext';
import { SidebarOrgSwitcher } from './orgSwitcher';
import Style from './app.tailwind.js';

export function AppNavItem(props) {
    const { item, isCollapsed } = props;
    const location = useLocation();

    const content = (
        <>
            {item.icon && (
                <Icon
                    image={item.icon}
                    size={18}
                    color="currentColor"
                    className="shrink-0"
                />
            )}
            {!isCollapsed && item.label && <span className={Style.itemLabel}>{item.label}</span>}
        </>
    );

    const classes = (isActive) =>
        cn(
            Style.item,
            isActive ? Style.itemActive : Style.itemIdle,
            isCollapsed && Style.itemCollapsed
        );

    let element;

    if (item.external || item.link?.includes('http')) {
        element = (
            <a
                href={item.link}
                target="_blank"
                rel="noreferrer"
                className={classes(false)}>
                {content}
            </a>
        );
    } else if (item.link) {
        element = (
            <NavLink to={item.link} className={({ isActive }) => classes(isActive)}>
                {content}
            </NavLink>
        );
    } else {
        // `location` is read here only so an action item can still light up when
        // it owns the current route; today none do, but the shape stays uniform.
        element = (
            <button type="button" onClick={item.action} className={classes(item.link === location.pathname)}>
                {content}
            </button>
        );
    }

    // Collapsed, the label is gone, so it moves into a tooltip. `side="right"`
    // because there is no collision-aware flipping — see components/tooltip.
    if (isCollapsed)
        return (
            <Tooltip label={item.label} side="right" delayDuration={250} className={Style.tooltip}>
                {element}
            </Tooltip>
        );

    return element;
}

export function AppNav(props) {
    const { _t } = useTranslate();
    const { isCollapsed } = useSidebarContext();

    return (
        <aside
            aria-label={_t('sidebar.regionLabel')}
            className={cn(Style.sidebar, isCollapsed ? Style.collapsed : Style.expanded)}>
            {/* Organisation identity — the reference's workspace slot (trap 6) */}
            <div className={Style.top}>
                <SidebarOrgSwitcher />
            </div>

            {/* id is what the top bar's toggle points aria-controls at */}
            <nav id="app-sidebar-nav" className={Style.nav}>
                {props.items?.map((item) => (
                    <AppNavItem
                        key={item.key || item.link || item.label}
                        item={item}
                        isCollapsed={isCollapsed}
                    />
                ))}
            </nav>

            {/* Valetify brand footer */}
            <div className={Style.brand}>
                <a href="https://valetify.io" target="_blank" rel="noreferrer" className="inline-flex items-center">
                    <img src="/assets/icon.png" alt="Valetify" className={Style.brandMark} />
                    {!isCollapsed && <span className={Style.brandLabel}>{_t('poweredBy')}</span>}
                </a>
            </div>
        </aside>
    );
}
